import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Shield, AlertCircle, Scale, Globe, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import SEOHead from './SEOHead';

const TermsOfUsePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedPath();

  const handleBackToHome = () => {
    navigate(getLocalizedPath('/'));
  };

  const sections = [
    { id: 'acceptance', icon: FileText },
    { id: 'description', icon: Globe },
    { id: 'accuracy', icon: AlertCircle },
    { id: 'intellectualProperty', icon: Shield },
    { id: 'limitations', icon: Scale },
    { id: 'modifications', icon: FileText },
    { id: 'governingLaw', icon: Scale },
    { id: 'contact', icon: Mail }
  ];

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('termsOfUse.pageTitle')}
        description={t('termsOfUse.pageDescription')}
      />

      {/* Back Button */}
      <button
        onClick={handleBackToHome}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common.backToAllConverters')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-4">
          <h1 className="text-xl font-semibold text-white">
            {t('termsOfUse.pageTitle')}
          </h1>
        </div>
        <div className="p-4">
          <p className="text-gray-600">
            {t('termsOfUse.lastUpdated')}: {t('termsOfUse.lastUpdatedDate')}
          </p>
          <p className="text-gray-600 mt-3">
            {t('termsOfUse.introduction')}
          </p>
        </div>
      </div>

      {/* Terms Sections */}
      {sections.map((section, index) => {
        const IconComponent = section.icon;
        return (
          <div
            key={section.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
              <div className="flex items-center gap-2">
                <IconComponent className="h-5 w-5 text-white" />
                <h2 className="text-lg font-semibold text-white">
                  {index + 1}. {t(`termsOfUse.sections.${section.id}.title`)}
                </h2>
              </div>
            </div>
            <div className="p-4">
              <p className="text-gray-600 text-sm whitespace-pre-line">
                {t(`termsOfUse.sections.${section.id}.content`)}
              </p>
              {section.id === 'accuracy' && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    <strong>{t('termsOfUse.disclaimer')}:</strong> {t('termsOfUse.disclaimerText')}
                  </p>
                </div>
              )}
              {section.id === 'contact' && (
                <div className="mt-3">
                  <a
                    href="mailto:contact@metricconverters.com"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <Mail className="h-4 w-4" />
                    contact@metricconverters.com
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Summary Box */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h2 className="text-lg font-semibold text-white">
            {t('termsOfUse.summaryTitle')}
          </h2>
        </div>
        <div className="p-4">
          <ul className="space-y-2">
            {t('termsOfUse.summaryPoints', { returnObjects: true }).map((point, index) => (
              <li key={index} className="flex items-start gap-2 text-gray-600 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4a7c9b] mt-1.5 flex-shrink-0"></span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;
